import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import Replay10Icon from '@mui/icons-material/Replay10';
import Forward10Icon from '@mui/icons-material/Forward10';
import Hls from 'hls.js';
import type { IProject } from '../../types/global/helper';
import { api } from '../../configurations/api';
import { cookieUrl } from '../../configurations/api/url';
import { httpRequest } from '../../redux/types';
import { OBSIDIAN, OBSIDIAN_BODY_FONT } from '../../constants/obsidianDashboardTheme';

const SPEEDS = [1, 1.25, 1.5, 2] as const;
const SURFACE_LOWEST = '#000000';

function getHlsSrc(project: IProject): string | null {
    const url = project.hls;
    if (url && /^https?:\/\//i.test(url)) return url;
    return null;
}

async function ensureProjectPlaybackCookie(videoId: string): Promise<void> {
    const id = String(videoId || '').trim();
    if (!id) return;
    await api(`${cookieUrl}/${id}`, httpRequest.GET, null, 2, 2000);
}

function formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const s = Math.floor(seconds);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
    return `${m}:${String(r).padStart(2, '0')}`;
}

export type SandboxHlsPlayerProps = {
    project: IProject;
    startSeconds?: number;
    onProgress?: (position: number, duration: number) => void;
    onCompleted?: (position: number, duration: number) => void;
    fillHeight?: boolean;
};

export function SandboxHlsPlayer({
    project,
    startSeconds = 0,
    onProgress,
    onCompleted,
    fillHeight,
}: SandboxHlsPlayerProps) {
    const shellRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const lastEmit = useRef(0);
    const scrubbing = useRef(false);
    const completedEmitted = useRef(false);
    const onProgressRef = useRef(onProgress);
    const onCompletedRef = useRef(onCompleted);
    onProgressRef.current = onProgress;
    onCompletedRef.current = onCompleted;
    const [isFs, setIsFs] = useState(false);
    const [ready, setReady] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState(0);
    const [buffered, setBuffered] = useState(0);
    const [speedIdx, setSpeedIdx] = useState(0);
    const [qualityLabel, setQualityLabel] = useState<string | null>(null);
    const [showChrome, setShowChrome] = useState(true);

    useEffect(() => {
        const onFsChange = () => {
            const el = document.fullscreenElement;
            setIsFs(Boolean(el && (el === shellRef.current || el === videoRef.current)));
        };
        document.addEventListener('fullscreenchange', onFsChange);
        return () => document.removeEventListener('fullscreenchange', onFsChange);
    }, []);

    const toggleFullscreen = useCallback(async () => {
        const shell = shellRef.current;
        const video = videoRef.current;
        if (!shell || !video) return;
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
                return;
            }
            if (shell.requestFullscreen) await shell.requestFullscreen();
            else if ((video as any).webkitEnterFullscreen) (video as any).webkitEnterFullscreen();
        } catch {
            /* ignore */
        }
    }, []);

    const togglePlay = useCallback(async () => {
        const video = videoRef.current;
        if (!video) return;
        try {
            if (video.paused) await video.play();
            else video.pause();
        } catch {
            setError('Could not start playback — try again');
        }
    }, []);

    const seekBy = useCallback((delta: number) => {
        const video = videoRef.current;
        if (!video || !Number.isFinite(video.duration)) return;
        const next = Math.max(0, Math.min(video.duration, video.currentTime + delta));
        scrubbing.current = true;
        video.currentTime = next;
        setCurrent(next);
        const clear = () => {
            scrubbing.current = false;
            video.removeEventListener('seeked', clear);
        };
        video.addEventListener('seeked', clear);
    }, []);

    const cycleSpeed = useCallback(() => {
        const video = videoRef.current;
        const next = (speedIdx + 1) % SPEEDS.length;
        setSpeedIdx(next);
        if (video) video.playbackRate = SPEEDS[next];
    }, [speedIdx]);

    const seekFromEvent = useCallback((clientX: number, track: HTMLElement) => {
        const video = videoRef.current;
        if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
        const rect = track.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const next = ratio * video.duration;
        video.currentTime = next;
        setCurrent(next);
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !project?._id) return;
        let cancelled = false;
        let markedReady = false;
        let nativeSeekDone = false;
        setReady(false);
        setPlaying(false);
        setLoading(true);
        setError(null);
        setCurrent(0);
        setDuration(0);
        setBuffered(0);
        setSpeedIdx(0);
        setQualityLabel(null);
        completedEmitted.current = false;
        lastEmit.current = 0;

        const hlsUrl = getHlsSrc(project);
        if (!hlsUrl) {
            setLoading(false);
            setError('No HLS stream for this lesson');
            return;
        }

        // Ignore tiny resumes (<2s) — early seeks on HLS often crackle for no benefit.
        const resumeAt = Number.isFinite(startSeconds) && startSeconds >= 2 ? Math.floor(startSeconds) : -1;

        const markReady = () => {
            if (cancelled || markedReady) return;
            markedReady = true;
            setReady(true);
            setLoading(false);
            setDuration(video.duration || 0);
            if (resumeAt > 0) setCurrent(resumeAt);
        };

        const fail = (message: string) => {
            if (cancelled) return;
            setLoading(false);
            setError(message);
        };

        const syncBuffer = () => {
            try {
                if (video.buffered.length > 0 && video.duration > 0) {
                    setBuffered(video.buffered.end(video.buffered.length - 1) / video.duration);
                }
            } catch {
                /* ignore */
            }
        };

        const onLoaded = () => markReady();
        const onPlay = () => setPlaying(true);
        const onWaiting = () => setLoading(true);
        const onPlaying = () => {
            setLoading(false);
            setPlaying(true);
        };
        const emitProgress = (pos: number, dur: number, force = false) => {
            const now = Date.now();
            if (!force && now - lastEmit.current < 4000) return;
            lastEmit.current = now;
            onProgressRef.current?.(pos, dur);
            // Near-end safety: some HLS streams never fire `ended`.
            if (!completedEmitted.current && dur > 0 && pos / dur >= 0.9) {
                completedEmitted.current = true;
                onCompletedRef.current?.(pos, dur);
            }
        };
        const onTime = () => {
            if (!scrubbing.current) setCurrent(video.currentTime);
            setDuration(video.duration || 0);
            syncBuffer();
            emitProgress(video.currentTime, video.duration || 0);
        };
        const onPauseFlush = () => {
            setPlaying(false);
            if (video.currentTime > 0) {
                emitProgress(video.currentTime, video.duration || 0, true);
            }
        };
        const onEnded = () => {
            setPlaying(false);
            const pos = video.duration || video.currentTime;
            const dur = video.duration || 0;
            emitProgress(pos, dur, true);
            if (!completedEmitted.current) {
                completedEmitted.current = true;
                onCompletedRef.current?.(pos, dur);
            }
        };
        const onVideoError = () => fail('Video failed to play');

        video.addEventListener('loadedmetadata', onLoaded);
        video.addEventListener('canplay', onLoaded);
        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPauseFlush);
        video.addEventListener('waiting', onWaiting);
        video.addEventListener('playing', onPlaying);
        video.addEventListener('timeupdate', onTime);
        video.addEventListener('progress', syncBuffer);
        video.addEventListener('ended', onEnded);
        video.addEventListener('error', onVideoError);

        const start = async () => {
            if (project.video_id) {
                try {
                    await ensureProjectPlaybackCookie(project.video_id);
                } catch {
                    /* still try HLS */
                }
            }
            if (cancelled) return;

            if (Hls.isSupported()) {
                const hls = new Hls({
                    enableWorker: true,
                    // Let hls.js land on the resume fragment instead of forcing currentTime
                    // during attach (that path commonly crackles audio).
                    startPosition: resumeAt,
                    maxBufferHole: 0.5,
                    nudgeMaxRetry: 5,
                    xhrSetup: xhr => {
                        xhr.withCredentials = true;
                    },
                });
                hlsRef.current = hls;
                hls.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
                    markReady();
                    const levels = data?.levels || [];
                    const best = levels[levels.length - 1];
                    if (best?.height) setQualityLabel(`${best.height}p`);
                });
                hls.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => {
                    const level = hls.levels?.[data.level];
                    if (level?.height) setQualityLabel(`${level.height}p`);
                });
                hls.on(Hls.Events.ERROR, (_event, data) => {
                    if (!data?.fatal) return;
                    fail(
                        'HLS CORS failed: CDN must allow your app origin with credentials (not Access-Control-Allow-Origin: *).',
                    );
                });
                hls.loadSource(hlsUrl);
                hls.attachMedia(video);
            } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
                // Safari native HLS: seek once after metadata, not on every canplay.
                const seekNativeOnce = () => {
                    if (nativeSeekDone || resumeAt <= 0 || cancelled) return;
                    nativeSeekDone = true;
                    try {
                        video.currentTime = resumeAt;
                        setCurrent(resumeAt);
                    } catch {
                        /* ignore */
                    }
                };
                video.addEventListener('loadedmetadata', seekNativeOnce, { once: true });
                video.src = hlsUrl;
            } else {
                fail('HLS playback is not supported in this browser');
            }
        };

        void start();

        // CloudFront signed-cookie policy is ~1h — refresh while the lesson stays open.
        const cookieRefresh =
            project.video_id &&
            window.setInterval(() => {
                void ensureProjectPlaybackCookie(String(project.video_id)).catch(() => undefined);
            }, 45 * 60 * 1000);

        return () => {
            cancelled = true;
            if (cookieRefresh) window.clearInterval(cookieRefresh);
            video.removeEventListener('loadedmetadata', onLoaded);
            video.removeEventListener('canplay', onLoaded);
            video.removeEventListener('play', onPlay);
            video.removeEventListener('pause', onPauseFlush);
            video.removeEventListener('waiting', onWaiting);
            video.removeEventListener('playing', onPlaying);
            video.removeEventListener('timeupdate', onTime);
            video.removeEventListener('progress', syncBuffer);
            video.removeEventListener('ended', onEnded);
            video.removeEventListener('error', onVideoError);
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            video.pause();
            video.removeAttribute('src');
            video.load();
        };
    }, [project?._id, project?.video_id, project?.hls, startSeconds]); // eslint-disable-line react-hooks/exhaustive-deps

    const playedPct = duration > 0 ? (current / duration) * 100 : 0;
    const bufferedPct = Math.min(100, buffered * 100);

    return (
        <Box
            ref={shellRef}
            onMouseMove={() => setShowChrome(true)}
            onMouseLeave={() => playing && setShowChrome(false)}
            sx={{
                width: '100%',
                aspectRatio: '16 / 9',
                ...(fillHeight
                    ? { height: '100%', minHeight: { md: 360 }, maxHeight: { md: 'min(70vh, 640px)' } }
                    : null),
                bgcolor: SURFACE_LOWEST,
                borderRadius: isFs ? 0 : 0,
                overflow: 'hidden',
                position: 'relative',
                userSelect: 'none',
                '&:fullscreen': {
                    borderRadius: 0,
                    width: '100vw',
                    height: '100vh',
                    aspectRatio: 'unset',
                    maxHeight: 'none',
                },
            }}>
            <Box
                component="video"
                ref={videoRef}
                controls={false}
                controlsList="nodownload"
                disablePictureInPicture
                playsInline
                onContextMenu={e => e.preventDefault()}
                onClick={() => void togglePlay()}
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    objectFit: 'contain',
                    bgcolor: SURFACE_LOWEST,
                }}
            />

            {/* Ambient vignette */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    background:
                        'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 42%), linear-gradient(to right, rgba(0,0,0,0.35) 0%, transparent 40%, rgba(0,0,0,0.25) 100%)',
                    opacity: showChrome || !playing ? 1 : 0,
                    transition: 'opacity 0.25s',
                    zIndex: 1,
                }}
            />

            {qualityLabel && !error ? (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        zIndex: 2,
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        opacity: showChrome || !playing ? 1 : 0,
                        transition: 'opacity 0.25s',
                    }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: OBSIDIAN.secondary }} />
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 10,
                            fontWeight: 600,
                            color: OBSIDIAN.secondary,
                        }}>
                        {qualityLabel}
                    </Typography>
                </Box>
            ) : null}

            {!error && ready && !loading && !playing ? (
                <IconButton
                    onClick={() => void togglePlay()}
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 56,
                        height: 56,
                        bgcolor: `${OBSIDIAN.surfaceContainerHighest}cc`,
                        backdropFilter: 'blur(10px)',
                        color: OBSIDIAN.primary,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                        zIndex: 3,
                        '&:hover': { bgcolor: OBSIDIAN.surfaceContainerHighest },
                    }}>
                    <PlayArrowIcon sx={{ fontSize: 32, ml: 0.25 }} />
                </IconButton>
            ) : null}

            {loading && !error ? (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        pointerEvents: 'none',
                    }}>
                    <CircularProgress size={36} sx={{ color: OBSIDIAN.primary }} />
                </Box>
            ) : null}

            {error ? (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 3,
                        bgcolor: 'rgba(0,0,0,0.72)',
                        zIndex: 4,
                    }}>
                    <Typography
                        sx={{
                            color: OBSIDIAN.onSurface,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 13,
                            textAlign: 'center',
                            maxWidth: 360,
                        }}>
                        {error}
                    </Typography>
                </Box>
            ) : null}

            {/* Bottom cinema controls */}
            {!error && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 3,
                        pt: 4,
                        pb: 1.25,
                        px: 1.5,
                        background: 'linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.7), transparent)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        opacity: showChrome || !playing ? 1 : 0,
                        transition: 'opacity 0.25s',
                        pointerEvents: showChrome || !playing ? 'auto' : 'none',
                    }}>
                    <Box
                        onClick={e => seekFromEvent(e.clientX, e.currentTarget)}
                        onMouseDown={e => {
                            scrubbing.current = true;
                            seekFromEvent(e.clientX, e.currentTarget);
                            const track = e.currentTarget;
                            const onMove = (ev: MouseEvent) => seekFromEvent(ev.clientX, track);
                            const onUp = () => {
                                scrubbing.current = false;
                                window.removeEventListener('mousemove', onMove);
                                window.removeEventListener('mouseup', onUp);
                            };
                            window.addEventListener('mousemove', onMove);
                            window.addEventListener('mouseup', onUp);
                        }}
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            '&:hover .scrub-thumb': { transform: 'translate(-50%, -50%) scale(1.25)' },
                        }}>
                        <Box
                            sx={{
                                width: '100%',
                                height: 4,
                                bgcolor: OBSIDIAN.surfaceVariant,
                                borderRadius: 999,
                                overflow: 'hidden',
                                position: 'relative',
                            }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    height: '100%',
                                    width: `${bufferedPct}%`,
                                    bgcolor: `${OBSIDIAN.outline}99`,
                                    borderRadius: 999,
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    height: '100%',
                                    width: `${playedPct}%`,
                                    bgcolor: OBSIDIAN.primary,
                                    borderRadius: 999,
                                    boxShadow: '0 0 8px rgba(255,201,101,0.8)',
                                }}
                            />
                        </Box>
                        <Box
                            className="scrub-thumb"
                            sx={{
                                position: 'absolute',
                                left: `${playedPct}%`,
                                top: '50%',
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: OBSIDIAN.primaryDim,
                                border: '1px solid rgba(255,255,255,0.4)',
                                boxShadow: '0 0 10px #ffc965',
                                transform: 'translate(-50%, -50%)',
                                transition: 'transform 0.15s',
                            }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <IconButton
                                size="small"
                                onClick={() => void togglePlay()}
                                sx={{ color: OBSIDIAN.onSurface, p: 0.5 }}>
                                {playing ? (
                                    <PauseIcon sx={{ fontSize: 20 }} />
                                ) : (
                                    <PlayArrowIcon sx={{ fontSize: 20 }} />
                                )}
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => seekBy(-10)}
                                sx={{ color: OBSIDIAN.onSurfaceVariant, p: 0.5 }}>
                                <Replay10Icon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => seekBy(10)}
                                sx={{ color: OBSIDIAN.onSurfaceVariant, p: 0.5 }}>
                                <Forward10Icon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Typography
                                sx={{
                                    ml: 0.5,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: OBSIDIAN.primary,
                                }}>
                                {formatTime(current)}
                                <Box component="span" sx={{ color: OBSIDIAN.outline, mx: 0.5 }}>
                                    /
                                </Box>
                                <Box component="span" sx={{ color: OBSIDIAN.onSurfaceVariant, fontWeight: 500 }}>
                                    {formatTime(duration)}
                                </Box>
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                                component="button"
                                type="button"
                                onClick={cycleSpeed}
                                sx={{
                                    border: 0,
                                    cursor: 'pointer',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 0.5,
                                    bgcolor: OBSIDIAN.surfaceContainerHighest,
                                    color: OBSIDIAN.primary,
                                    fontFamily: OBSIDIAN_BODY_FONT,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    '&:hover': { bgcolor: '#2c2c2c' },
                                }}>
                                {SPEEDS[speedIdx]}x
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => void toggleFullscreen()}
                                sx={{ color: OBSIDIAN.onSurface, p: 0.5 }}>
                                {isFs ? (
                                    <FullscreenExitIcon sx={{ fontSize: 19 }} />
                                ) : (
                                    <FullscreenIcon sx={{ fontSize: 19 }} />
                                )}
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
